import React, { type ReactElement, useEffect, useRef, useState } from 'react';

import classnames from 'classnames';
import { decode } from 'entities';

import safeNumber from '../../utils/safeNumber';
import { Button } from '../Button';
import { type IconColor, type IconName } from '../Icon';
import { Icon } from '../Icon/Icon';
import { Spinner } from '../Spinner';

import styles from './Snackbar.module.scss';

const STANDARD_DISMISS_AFTER = 6000;
const STANDARD_MESSAGE_LENGTH = 120;

export enum SnackbarType {
    Error = 'error',
    Information = 'information',
    Success = 'success',
    Warning = 'warning'
}

export type SnackbarProps = {
    button?: {
        action: () => void;
        dismissViaAction?: boolean;
        text: string;
    };
    dismissAfter?: number;
    dismissAllowed?: boolean;
    header?: string;
    id?: string;
    loading?: boolean;
    message: string;
    onDismiss: () => void;
    hideCloseButton?: boolean;
    type: SnackbarType;
    width?: number;
};

const DISMISS_ANIMATION_DURATION = 300;

export const Snackbar = ({
    button,
    dismissAfter,
    dismissAllowed = true,
    header,
    id,
    loading,
    message,
    onDismiss,
    hideCloseButton,
    type,
    width
}: SnackbarProps): ReactElement => {
    const [isDismissed, setIsDismissed] = useState(false);
    const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const iconNameMap: Record<SnackbarType, IconName> = {
        [SnackbarType.Error]: 's-icon-circle-exclamation',
        [SnackbarType.Success]: 's-icon-circle-check',
        [SnackbarType.Warning]: 's-icon-circle-exclamation',
        [SnackbarType.Information]: 's-icon-circle-info'
    };
    const iconColorMap: Record<SnackbarType, IconColor> = {
        [SnackbarType.Error]: 'danger',
        [SnackbarType.Success]: 'success',
        [SnackbarType.Warning]: 'warning',
        [SnackbarType.Information]: 'information'
    };
    const iconName = iconNameMap[type];
    const iconColor = iconColorMap[type];

    const notificationClassNames = (className: string, notificationType: SnackbarType) =>
        classnames(className, {
            [styles.danger]: notificationType === SnackbarType.Error,
            [styles.success]: notificationType === SnackbarType.Success,
            [styles.warning]: notificationType === SnackbarType.Warning,
            [styles.information]: notificationType === SnackbarType.Information
        });

    const calculateDismissTime = (messageLength: number) => {
        if (messageLength <= STANDARD_MESSAGE_LENGTH) {
            return STANDARD_DISMISS_AFTER;
        }
        return (
            STANDARD_DISMISS_AFTER +
            Math.floor(((messageLength - STANDARD_MESSAGE_LENGTH) * 1000) / STANDARD_MESSAGE_LENGTH)
        );
    };

    const handleDismiss = () => {
        setIsDismissed(true);
        animationTimeoutRef.current = setTimeout(() => {
            onDismiss();
        }, DISMISS_ANIMATION_DURATION);
    };

    const handleButtonAction = () => {
        if (button?.dismissViaAction) {
            setIsDismissed(true);
            animationTimeoutRef.current = setTimeout(() => {
                button?.action();
            }, DISMISS_ANIMATION_DURATION);
        } else {
            button?.action();
        }
    };

    useEffect(() => {
        if (dismissAfter) {
            timeoutRef.current = setTimeout(() => handleDismiss(), dismissAfter);
        } else if (dismissAllowed) {
            timeoutRef.current = setTimeout(
                handleDismiss,
                calculateDismissTime(
                    header
                        ? header.length + safeNumber(message?.length)
                        : safeNumber(message?.length)
                )
            );
        }
        return () => {
            if (timeoutRef?.current) {
                clearTimeout(timeoutRef.current);
            }
            if (animationTimeoutRef?.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [animationTimeoutRef, dismissAfter, dismissAllowed, header, message, timeoutRef]);

    return (
        <div
            className={classnames(styles.notification, { [styles.isDismissed]: isDismissed })}
            id={id}
            style={{ width }}
        >
            <div className={notificationClassNames(styles.colorMargin, type)} />
            <div className={classnames(styles.icon, { [styles.longerContent]: header })}>
                {loading ? (
                    <Spinner aria-label="spinner" size={24} />
                ) : (
                    <Icon name={iconName} color={iconColor} size="large" />
                )}
            </div>
            <div className={styles.content}>
                {header && <div className={styles.header}>{header}</div>}
                <div className={classnames(styles.message, { [styles.withoutHeader]: !header })}>
                    {decode(message ?? '')}
                </div>
            </div>
            {button && (
                <Button
                    size="medium"
                    className={classnames(styles.notificationButton, {
                        [styles.longerContent]: header
                    })}
                    onClick={handleButtonAction}
                    type="outlined"
                >
                    {button.text}
                </Button>
            )}
            {!hideCloseButton && (
                <Icon
                    aria-label="close notification"
                    btnClassName={classnames(styles.notificationCloseIcon, {
                        [styles.longerContent]: header
                    })}
                    onClick={handleDismiss}
                    name="s-icon-xmark"
                />
            )}
        </div>
    );
};

Snackbar.displayName = 'Snackbar';
